import { DEFAULT_THEME, PALETTE_TO_CSSVAR, type Theme, type ThemePalette } from "@hyggebam/shared";

/**
 * Runtime theming. A Theme is applied by writing its tokens onto :root as CSS
 * custom properties — the whole UI re-skins instantly with no re-render, because
 * every themeable value in styles.css references one of these tokens.
 */
const THEME_KEY = "hyggebam.theme";

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  for (const key of Object.keys(PALETTE_TO_CSSVAR) as (keyof ThemePalette)[]) {
    root.style.setProperty(PALETTE_TO_CSSVAR[key], theme.palette[key]);
  }
  root.style.setProperty("--radius", `${theme.radius}px`);

  if (theme.font) {
    ensureFont(theme.font);
    root.style.setProperty("--font", `'${theme.font}', -apple-system, system-ui, sans-serif`);
  } else {
    root.style.removeProperty("--font"); // fall back to the default stack in :root
  }

  root.style.setProperty("--theme-bg-image", theme.images.background ? `url("${theme.images.background}")` : "none");
  root.style.setProperty("--theme-accent-image", theme.images.accent ? `url("${theme.images.accent}")` : "none");
}

function ensureFont(family: string): void {
  const id = "themefont-" + family.replace(/[^a-z0-9]+/gi, "-");
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;600;700;800&display=swap`;
  document.head.appendChild(link);
}

// ── Per-device persistence ──────────────────────────────────────────────────
export function storeTheme(theme: Theme): void {
  localStorage.setItem(THEME_KEY, JSON.stringify(theme));
}
export function getStoredTheme(): Theme | null {
  try {
    const s = localStorage.getItem(THEME_KEY);
    return s ? (JSON.parse(s) as Theme) : null;
  } catch {
    return null;
  }
}
export function clearStoredTheme(): void {
  localStorage.removeItem(THEME_KEY);
}

/** Apply the best theme we know about as early as possible (called at boot). */
export function bootTheme(): void {
  applyTheme(getStoredTheme() ?? DEFAULT_THEME);
}
